import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { formatAmount } from '../datasets/utils/formatters';
import { FeeComparisonTable } from './components/FeeComparisonTable';
import { QuoteBreakdown } from './components/QuoteBreakdown';

interface Quotation {
  id: string;
  name: string;
  created_at: string;
  bank: {
    name: string;
    alias: string;
  };
  estimated_payments: number;
  estimated_payouts: number;
  average_amount: number;
  payment_approved_fixed_fee: number;
  payment_approved_percentage_fee: number;
  payment_approved_max_cap: number;
  payment_declined_fixed_fee: number;
  payment_declined_percentage_fee: number;
  payment_declined_max_cap: number;
  payment_returned_fixed_fee: number;
  payment_returned_percentage_fee: number;
  payment_returned_max_cap: number;
  payout_approved_fixed_fee: number;
  payout_approved_percentage_fee: number;
  payout_approved_max_cap: number;
  payout_declined_fixed_fee: number;
  payout_declined_percentage_fee: number;
  payout_declined_max_cap: number;
  payout_returned_fixed_fee: number;
  payout_returned_percentage_fee: number;
  payout_returned_max_cap: number;
}

interface FeeBreakdown {
  merchant: {
    fixed: number;
    percentage: number;
    beforeCap: number;
    afterCap: number;
    capApplied: boolean;
  };
  bank: {
    fixed: number;
    percentage: number;
    beforeCap: number;
    afterCap: number;
    capApplied: boolean;
  };
  profit: number;
}

export function QuoteComparison() {
  const { quoteIds } = useParams();
  const [quotes, setQuotes] = useState<Quotation[]>([]);
  const [bankFees, setBankFees] = useState<Record<string, BankFee[]>>({});
  const [breakdowns, setBreakdowns] = useState<Record<string, {
    payment: FeeBreakdown;
    payout: FeeBreakdown;
  }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (quoteIds) {
      loadQuotes();
    }
  }, [quoteIds]);

  const loadQuotes = async () => {
    try {
      const ids = quoteIds?.split(',');
      if (!ids?.length || ids.length !== 2) {
        throw new Error('Invalid comparison parameters');
      }

      // Fetch quotes and bank fees in parallel
      const [quotesResult, bankFeesResult] = await Promise.all([
        supabase
        .from('quotations')
        .select(`
          *,
          bank:banks (
            id,
            name,
            alias
          )
        `)
        .in('id', ids),
        supabase
          .from('bank_fees')
          .select('*')
      ]);

      if (quotesResult.error) throw quotesResult.error;
      if (bankFeesResult.error) throw bankFeesResult.error;
      if (!quotesResult.data || quotesResult.data.length !== 2) {
        throw new Error('Could not load both quotations');
      }

      // Group bank fees by bank_id for easier lookup
      const feesByBank = bankFeesResult.data.reduce((acc, fee) => {
        if (!acc[fee.bank_id]) {
          acc[fee.bank_id] = [];
        }
        acc[fee.bank_id].push(fee);
        return acc;
      }, {} as Record<string, BankFee[]>);

      setQuotes(quotesResult.data);
      
      // Calculate fee breakdowns for each quote
      const newBreakdowns: Record<string, any> = {};
      quotesResult.data.forEach(quote => {
        const bankPaymentFees = quote.bank?.id 
          ? bankFeesResult.data.find(f => 
              f.bank_id === quote.bank.id && 
              f.payment_method === 'etransfer_request_money' && 
              f.status === 'approved'
            )
          : null;

        const bankPayoutFees = quote.bank?.id
          ? bankFeesResult.data.find(f => 
              f.bank_id === quote.bank.id && 
              f.payment_method === 'etransfer_send_money' && 
              f.status === 'approved'
            )
          : null;

        newBreakdowns[quote.id] = {
          payment: calculateFeeBreakdown(
            quote.average_amount,
            quote.estimated_payments,
            quote.payment_approved_fixed_fee,
            quote.payment_approved_percentage_fee,
            quote.payment_approved_max_cap,
            bankPaymentFees?.per_transaction_fee || 0,
            bankPaymentFees?.rate_fee_percentage || 0,
            bankPaymentFees?.max_fee_cap || 0
          ),
          payout: calculateFeeBreakdown(
            quote.average_amount,
            quote.estimated_payouts,
            quote.payout_approved_fixed_fee,
            quote.payout_approved_percentage_fee,
            quote.payout_approved_max_cap,
            bankPayoutFees?.per_transaction_fee || 0,
            bankPayoutFees?.rate_fee_percentage || 0,
            bankPayoutFees?.max_fee_cap || 0
          )
        };
      });
      setBreakdowns(newBreakdowns);
      setBankFees(feesByBank);
    } catch (err) {
      console.error('Error loading quotations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const getBankFees = (quote: Quotation, method: string, status: string) => {
    if (!quote.bank?.id) return null;
    
    // Get bank fees for this bank
    const fees = bankFees[quote.bank.id];
    if (!fees) return null;

    // Find the specific fee for this method and status
    const fee = bankFees[quote.bank.id].find(
      f => f.payment_method === method && f.status === status
    );

    return fee || null;
  };

  const getQuoteBankFees = (quote: Quotation) => {
    if (!quote.bank?.id) return null;

    // Get bank fees for this bank
    const fees = bankFees[quote.bank.id];
    if (!fees) return null;

    return {
      approved: {
        fixedFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'approved')?.per_transaction_fee || 0,
        percentageFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'approved')?.rate_fee_percentage || 0,
        maxCap: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'approved')?.max_fee_cap || 0
      },
      declined: {
        fixedFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'declined')?.per_transaction_fee || 0,
        percentageFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'declined')?.rate_fee_percentage || 0,
        maxCap: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'declined')?.max_fee_cap || 0
      },
      returned: {
        fixedFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'returned')?.per_transaction_fee || 0,
        percentageFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'returned')?.rate_fee_percentage || 0,
        maxCap: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'returned')?.max_fee_cap || 0
      }
    };
  };

  const getQuotePayoutBankFees = (quote: Quotation) => {
    if (!quote.bank?.id) return null;

    // Get bank fees for this bank
    const fees = bankFees[quote.bank.id];
    if (!fees) return null;

    return {
      approved: {
        fixedFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'approved')?.per_transaction_fee || 0,
        percentageFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'approved')?.rate_fee_percentage || 0,
        maxCap: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'approved')?.max_fee_cap || 0
      },
      declined: {
        fixedFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'declined')?.per_transaction_fee || 0,
        percentageFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'declined')?.rate_fee_percentage || 0,
        maxCap: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'declined')?.max_fee_cap || 0
      },
      returned: {
        fixedFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'returned')?.per_transaction_fee || 0,
        percentageFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'returned')?.rate_fee_percentage || 0,
        maxCap: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'returned')?.max_fee_cap || 0
      }
    };
  };

  const calculateRevenue = (quotation: Quotation) => {
    const calculateFee = (
      amount: number,
      count: number,
      fixedFee: number,
      percentageFee: number,
      maxCap: number
    ) => {
      const totalFixed = fixedFee * count;
      const totalPercentage = (amount * percentageFee / 100) * count;
      const totalBeforeCap = totalFixed + totalPercentage;
      return maxCap > 0 ? Math.min(totalBeforeCap, maxCap * count) : totalBeforeCap;
    };

    const paymentRevenue = calculateFee(
      quotation.average_amount,
      quotation.estimated_payments,
      quotation.payment_approved_fixed_fee,
      quotation.payment_approved_percentage_fee,
      quotation.payment_approved_max_cap
    );

    const payoutRevenue = calculateFee(
      quotation.average_amount,
      quotation.estimated_payouts,
      quotation.payout_approved_fixed_fee,
      quotation.payout_approved_percentage_fee,
      quotation.payout_approved_max_cap
    );

    return paymentRevenue + payoutRevenue;
  };

  const calculateFeeBreakdown = (
    amount: number,
    count: number,
    approvedFixedFee: number,
    approvedPercentageFee: number,
    approvedMaxCap: number,
    bankFixedFee: number,
    bankPercentageFee: number,
    bankMaxCap: number
  ) => {
    // Calculate merchant fees
    const totalFixed = approvedFixedFee * count;
    const totalPercentage = (amount * approvedPercentageFee / 100) * count;
    const totalBeforeCap = totalFixed + totalPercentage;
    const totalAfterCap = approvedMaxCap > 0 ? Math.min(totalBeforeCap, approvedMaxCap * count) : totalBeforeCap;

    // Calculate bank fees
    const bankTotalFixed = bankFixedFee * count;
    const bankTotalPercentage = (amount * bankPercentageFee / 100) * count;
    const bankTotalBeforeCap = bankTotalFixed + bankTotalPercentage;
    const bankTotalAfterCap = bankMaxCap > 0 ? Math.min(bankTotalBeforeCap, bankMaxCap * count) : bankTotalBeforeCap;

    return {
      merchant: {
        fixed: totalFixed,
        percentage: totalPercentage,
        beforeCap: totalBeforeCap,
        afterCap: totalAfterCap,
        capApplied: approvedMaxCap > 0 && totalBeforeCap > (approvedMaxCap * count)
      },
      bank: {
        fixed: bankTotalFixed,
        percentage: bankTotalPercentage,
        beforeCap: bankTotalBeforeCap,
        afterCap: bankTotalAfterCap,
        capApplied: bankMaxCap > 0 && bankTotalBeforeCap > (bankMaxCap * count)
      },
      profit: totalAfterCap - bankTotalAfterCap
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || quotes.length !== 2) {
    return (
      <div className="text-center py-12">
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
          {error || 'Could not load quotations for comparison'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link
          to="/quotations"
          className="flex items-center text-sm text-indigo-600 hover:text-indigo-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Quotations
        </Link>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h1 className="text-2xl font-bold text-gray-900">
            Quote Comparison
          </h1>
        </div>

        <div className="p-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {quotes.map((quote) => (
              <QuoteBreakdown
                key={quote.id}
                quote={quote}
                breakdowns={breakdowns[quote.id]}
                bankFees={{
                  getPaymentFees: (method, status) => getBankFees(quote, method, status),
                  getPayoutFees: (method, status) => getBankFees(quote, method, status)
                }}
              />
            ))}
          </div>

          {/* Fee Comparison */}
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-gray-900">Fee Structure Comparison</h2>
            
            {/* Payment Fee Comparison */}
            <FeeComparisonTable
              title="Payment Fees (eTransfer Request Money)"
              quote1={{
                name: quotes[0].name,
                fees: {
                  approved: {
                    fixedFee: quotes[0].payment_approved_fixed_fee,
                    percentageFee: quotes[0].payment_approved_percentage_fee,
                    maxCap: quotes[0].payment_approved_max_cap
                  },
                  declined: {
                    fixedFee: quotes[0].payment_declined_fixed_fee,
                    percentageFee: quotes[0].payment_declined_percentage_fee,
                    maxCap: quotes[0].payment_declined_max_cap
                  },
                  returned: {
                    fixedFee: quotes[0].payment_returned_fixed_fee,
                    percentageFee: quotes[0].payment_returned_percentage_fee,
                    maxCap: quotes[0].payment_returned_max_cap
                  }
                },
                bankFees: getQuoteBankFees(quotes[0])
              }}
              quote2={{
                name: quotes[1].name,
                fees: {
                  approved: {
                    fixedFee: quotes[1].payment_approved_fixed_fee,
                    percentageFee: quotes[1].payment_approved_percentage_fee,
                    maxCap: quotes[1].payment_approved_max_cap
                  },
                  declined: {
                    fixedFee: quotes[1].payment_declined_fixed_fee,
                    percentageFee: quotes[1].payment_declined_percentage_fee,
                    maxCap: quotes[1].payment_declined_max_cap
                  },
                  returned: {
                    fixedFee: quotes[1].payment_returned_fixed_fee,
                    percentageFee: quotes[1].payment_returned_percentage_fee,
                    maxCap: quotes[1].payment_returned_max_cap
                  }
                },
                bankFees: getQuoteBankFees(quotes[1])
              }}
            />

            {/* Payout Fee Comparison */}
            <FeeComparisonTable
              title="Payout Fees (eTransfer Send Money)"
              quote1={{
                name: quotes[0].name,
                fees: {
                  approved: {
                    fixedFee: quotes[0].payout_approved_fixed_fee,
                    percentageFee: quotes[0].payout_approved_percentage_fee,
                    maxCap: quotes[0].payout_approved_max_cap
                  },
                  declined: {
                    fixedFee: quotes[0].payout_declined_fixed_fee,
                    percentageFee: quotes[0].payout_declined_percentage_fee,
                    maxCap: quotes[0].payout_declined_max_cap
                  },
                  returned: {
                    fixedFee: quotes[0].payout_returned_fixed_fee,
                    percentageFee: quotes[0].payout_returned_percentage_fee,
                    maxCap: quotes[0].payout_returned_max_cap
                  }
                },
                bankFees: getQuotePayoutBankFees(quotes[0])
              }}
              quote2={{
                name: quotes[1].name,
                fees: {
                  approved: {
                    fixedFee: quotes[1].payout_approved_fixed_fee,
                    percentageFee: quotes[1].payout_approved_percentage_fee,
                    maxCap: quotes[1].payout_approved_max_cap
                  },
                  declined: {
                    fixedFee: quotes[1].payout_declined_fixed_fee,
                    percentageFee: quotes[1].payout_declined_percentage_fee,
                    maxCap: quotes[1].payout_declined_max_cap
                  },
                  returned: {
                    fixedFee: quotes[1].payout_returned_fixed_fee,
                    percentageFee: quotes[1].payout_returned_percentage_fee,
                    maxCap: quotes[1].payout_returned_max_cap
                  }
                },
                bankFees: getQuotePayoutBankFees(quotes[1])
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}